import { Device, Document } from "@models";
import { RoadmapVisibilityConditions } from "src/stores/models/roadmap-visibility-conditions";
import {
  hasElectrical,
  hasFirmware,
  hasHardware,
  hasImplantableComponent,
  hasMechanical,
  hasSoftware,
  hasSterileComponent,
  isClass1,
  isClass2a,
  isClass2b,
  isClass3,
  isSoftwareOnly,
} from "src/utils/device";
import { mapDocumentVersionsToDataKeys } from "src/utils/tasks";

/**
 * Any yup model that has `visibilityConditions` property can use this function to check if the conditions are met.
 * e.g. RoadmapCard, RoadmapTask, DocumentTemplate, DataKey
 *
 * Only conditions that are defined will be used and are OR'd against eachother.
 *
 * @param conditions - The visibility conditions to check
 * @param device - The device to check the visibility conditions for
 * @param documents - The documents to check the visibility conditions for
 * @returns True if the visibility conditions are met, false otherwise
 */
export const meetsVisibilityConditions = (
  conditions: RoadmapVisibilityConditions | undefined | null,
  device: Device,
  documents: Document[]
) => {
  if (!conditions || Object.keys(conditions).length === 0) {
    return true;
  }

  const availableDataKeys = mapDocumentVersionsToDataKeys(documents);

  const deviceClassConditions =
    (conditions.isClass1 !== undefined &&
      conditions.isClass1 === isClass1(availableDataKeys)) ||
    (conditions.isClass2a !== undefined &&
      conditions.isClass2a === isClass2a(availableDataKeys)) ||
    (conditions.isClass2b !== undefined &&
      conditions.isClass2b === isClass2b(availableDataKeys)) ||
    (conditions.isClass3 !== undefined &&
      conditions.isClass3 === isClass3(availableDataKeys));

  const deviceTypeConditions =
    (conditions.isMDR !== undefined && conditions.isMDR === device.isMDR) ||
    (conditions.isIVDR !== undefined && conditions.isIVDR === device.isIVDR) ||
    (conditions.isFDA !== undefined && conditions.isFDA === device.isFDA) ||
    (conditions.isUK !== undefined && conditions.isUK === device.isUK) ||
    (conditions.isCanada !== undefined &&
      conditions.isCanada === device.isCanada);

  const componentConditions =
    (conditions.hasElectrical !== undefined &&
      conditions.hasElectrical === hasElectrical(device)) ||
    (conditions.hasImplantable !== undefined &&
      conditions.hasImplantable === hasImplantableComponent(device)) ||
    (conditions.hasMechanical !== undefined &&
      conditions.hasMechanical === hasMechanical(device)) ||
    (conditions.isSoftwareOnly !== undefined &&
      conditions.isSoftwareOnly === isSoftwareOnly(device)) ||
    (conditions.hasSoftware !== undefined &&
      conditions.hasSoftware === hasSoftware(device)) ||
    (conditions.hasHardware !== undefined &&
      conditions.hasHardware === hasHardware(device)) ||
    (conditions.hasFirmware !== undefined &&
      conditions.hasFirmware === hasFirmware(device)) ||
    (conditions.hasSterile !== undefined &&
      conditions.hasSterile === hasSterileComponent(device));

  return deviceClassConditions || deviceTypeConditions || componentConditions;
};
